import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, createSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { partition } from 'lodash';
import * as yup from 'yup';
import { Button, FieldMessageType, Typography, useToast } from '@shipengine/giger';
import { FormField } from '@shipengine/formik-giger';
import { ClientUpdateOnboardingParams, Integration } from '@packlink/packlink-sdk';
import {
    FormikPhoneNumber,
    getPhonePrefixNumberValidation,
    PhoneNumberValue,
    useCountryPrefixOptions,
} from '@packlink/invoice-form';
import { Trans, useTranslation } from '@packlink/translation-provider';
import Packlink from '@sdk';
import { getNonHiddenIntegrations } from '@Integrations/services/integrations-service';
import { useIntegrations } from '@common/hooks/useIntegrations';
import { getClientData } from '@store/selectors/client';
import { useAmplitude } from '@common/hooks';
import { APP_ROUTE } from '@router/routes';
import { AmplitudeEvents } from '@constants/amplitude';
import { Box } from '../../components/Box/Box';
import {
    getButtonStyles,
    getNoStoreContentStyles,
    getNoStoreStyles,
    getPhoneFieldStyles,
    getPlatformsStyles,
    getPlatformsWrapperStyles,
    getSubtitleStyles,
    getTitleStyles,
} from './OnboardingPlatformsStyles';
import { OnboardingPlatformsCard } from './components/OnboardingPlatformsCard';

type PhoneNumberFormFields = {
    phone: PhoneNumberValue;
};

const OTHER_STORE_ALIAS = 'Other';
const NO_STORE_ALIAS = 'no-store';
const EMPTY_ECOMMERCE = 'I do not use an ecommerce platform';
const EMPTY_MARKETPLACE = 'I do not have any marketplace outlets';

export function OnboardingPlatforms(): JSX.Element {
    const { sendAmplitudeClickEvent } = useAmplitude();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast(t);
    const { integrations } = useIntegrations();
    const client = useSelector(getClientData);
    const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

    const {
        cdn: { url: cdnUrl },
    } = config;

    const { options: phonePrefixOptions, defaultValue } = useCountryPrefixOptions(
        config.cdn.baseUrl,
        client.alpha2Code,
        true,
    );

    const { mutate: updateOnboarding } = useMutation({
        mutationFn: (params: ClientUpdateOnboardingParams) => Packlink.v1.clients.updateOnboarding(params),
    });

    const validationSchema = yup.object().shape({
        phone: getPhonePrefixNumberValidation(t, true),
    });

    const filteredIntegrations = integrations?.filter(getNonHiddenIntegrations) || [];
    const displayIntegrations =
        filteredIntegrations.length > 9
            ? [
                  ...filteredIntegrations.slice(0, 8),
                  { alias: OTHER_STORE_ALIAS, name: t('onboarding.platforms.other-store'), logo: '' },
              ]
            : filteredIntegrations;

    const displayPhoneInput = selectedPlatforms.length > 0 && !selectedPlatforms.includes(NO_STORE_ALIAS);

    const modifyPlatformsSelection = (alias: string, isSelected: boolean) => {
        setSelectedPlatforms((prevPlatforms) => {
            if (alias === NO_STORE_ALIAS) {
                return isSelected ? [] : [NO_STORE_ALIAS];
            } else {
                const filteredPlatforms = prevPlatforms.filter((platform) => platform !== NO_STORE_ALIAS);

                if (isSelected) {
                    return filteredPlatforms.filter((platform) => platform !== alias);
                } else {
                    return [...filteredPlatforms, alias];
                }
            }
        });
    };

    const submitForm = ({ phone }: PhoneNumberFormFields, { setSubmitting }: FormikHelpers<PhoneNumberFormFields>) => {
        const phoneNumber = phone.number ? phone.prefix.value + phone.number : undefined;
        const marketplaces = [];
        const ecommerces = [];
        const selectedPlatformsByAlias = Object.fromEntries(selectedPlatforms.map((key) => [key, true]));

        if (selectedPlatformsByAlias[NO_STORE_ALIAS]) {
            marketplaces.push(EMPTY_MARKETPLACE);
            ecommerces.push(EMPTY_ECOMMERCE);
        } else {
            const selectedIntegrations = integrations?.filter(
                (integration) => selectedPlatformsByAlias[integration.alias],
            );

            const [selectedMarketplaces, selectedEcommerces] = partition(selectedIntegrations, (i) => i.isMarketplace);
            marketplaces.push(...selectedMarketplaces.map((m) => m.name));
            ecommerces.push(...selectedEcommerces.map((e) => e.name));

            if (selectedPlatformsByAlias[OTHER_STORE_ALIAS]) {
                marketplaces.push(OTHER_STORE_ALIAS);
                ecommerces.push(OTHER_STORE_ALIAS);
            }

            if (marketplaces.length === 0) {
                marketplaces.push(EMPTY_MARKETPLACE);
            }

            if (ecommerces.length === 0) {
                ecommerces.push(EMPTY_ECOMMERCE);
            }
        }

        const highestRankIntegration = integrations?.find((integration) => selectedPlatformsByAlias[integration.alias]);
        const proposedConnection = highestRankIntegration
            ? highestRankIntegration.alias
            : selectedPlatformsByAlias[OTHER_STORE_ALIAS]
              ? OTHER_STORE_ALIAS
              : undefined;

        updateOnboarding(
            { marketplaces, ecommerces, phone: phoneNumber },
            {
                onSuccess: () => {
                    if (proposedConnection) {
                        sendAmplitudeClickEvent(AmplitudeEvents.ONBOARDING_PLATFORMS_STORES, {
                            stores: selectedPlatforms,
                            phone: phoneNumber ? 'yes' : 'no',
                        });
                    } else {
                        sendAmplitudeClickEvent(AmplitudeEvents.ONBOARDING_PLATFORMS_NO_STORES);
                    }

                    return navigate({
                        pathname: APP_ROUTE.ONBOARDING.COMPLETED,
                        search: proposedConnection
                            ? createSearchParams({ connect: proposedConnection }).toString()
                            : undefined,
                    });
                },
                onError: () => toast.error({ message: t('error.message.try-again-later') }),
                onSettled: () => setSubmitting(false),
            },
        );
    };

    return (
        <Formik
            initialValues={{
                phone: {
                    prefix: {
                        alpha2Code: defaultValue?.alpha2Code ?? '',
                        value: defaultValue?.value ?? '',
                        label: defaultValue?.label ?? '',
                    },
                    number: '',
                },
            }}
            onSubmit={submitForm}
            validationSchema={validationSchema}
            validateOnChange
            enableReinitialize
        >
            {({ isSubmitting }: FormikProps<PhoneNumberFormFields>) => (
                <Form>
                    <Box>
                        <Typography css={getTitleStyles} variant="heading4" component="h4">
                            {t('onboarding.platforms.title')}
                        </Typography>
                        <Typography css={getSubtitleStyles} variant="body2" component="p">
                            {t('onboarding.platforms.subtitle')}
                        </Typography>

                        <div css={getPlatformsWrapperStyles}>
                            <div css={getPlatformsStyles}>
                                {displayIntegrations?.map(
                                    (
                                        integration: Integration | Pick<Integration, 'alias' | 'name' | 'logo'>,
                                    ): JSX.Element => {
                                        const { alias, name, logo } = integration;

                                        return (
                                            <OnboardingPlatformsCard
                                                key={alias}
                                                alias={alias}
                                                isSelected={selectedPlatforms.includes(alias)}
                                                logoUrl={logo}
                                                name={name}
                                                onClick={modifyPlatformsSelection}
                                            />
                                        );
                                    },
                                )}
                            </div>

                            {displayPhoneInput && (
                                <FormField
                                    css={getPhoneFieldStyles}
                                    name="phone"
                                    message={{
                                        type: FieldMessageType.HINT,
                                        content: <Trans i18nKey="onboarding.platforms.phone-help" />,
                                    }}
                                >
                                    <Field
                                        name="phone"
                                        component={FormikPhoneNumber}
                                        prefixOptions={phonePrefixOptions}
                                        cdnUrl={config.cdn.baseUrl}
                                        isOptional
                                    />
                                </FormField>
                            )}
                        </div>

                        <OnboardingPlatformsCard
                            css={getNoStoreStyles}
                            isSelected={selectedPlatforms.includes(NO_STORE_ALIAS)}
                            onClick={modifyPlatformsSelection}
                            alias={NO_STORE_ALIAS}
                            name={t('onboarding.platforms.no-stores-title')}
                        >
                            <img src={`${cdnUrl}/pro/statics/images/client-laptop.svg`} alt="" aria-hidden={true} />
                            <div css={getNoStoreContentStyles}>
                                <Typography css={getSubtitleStyles} variant="body2" component="p" bold>
                                    {t('onboarding.platforms.no-stores-title')}
                                </Typography>
                                <Typography css={getSubtitleStyles} variant="body2" component="p">
                                    {t('onboarding.platforms.no-stores-subtitle')}
                                </Typography>
                            </div>
                        </OnboardingPlatformsCard>

                        <Button
                            css={getButtonStyles}
                            type="submit"
                            isLoading={isSubmitting}
                            disabled={selectedPlatforms.length === 0}
                        >
                            {t('onboarding.platforms.button')}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    );
}
